import { FunctionField } from "react-admin";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { colors, fontFamilyMono } from "../../data/tokens";
import { initials } from "../../util/text";
import { socioPhotoUrl } from "../../util/socioPhoto";
import { formatPhone } from "../../util/phone";
import { EllipsisTip } from "../../components/EllipsisTip";

// Dados de apoio do sócio (CPF, CREA, celular…). Usam o tom intermediário
// dos tokens: o textMuted anterior, a 11px, ficava no limite de contraste.
const metaSx = {
  fontSize: 12,
  color: colors.textSecondary,
  whiteSpace: "nowrap" as const,
};

function maritalLabel(v?: string) {
  if (!v) return "";
  if (v === "UNIAO_ESTAVEL") return "União Estável";
  return v.charAt(0).toUpperCase() + v.toLowerCase().slice(1) + "(a)";
}

// Bloco do sócio no padrão remetente: avatar + nome em destaque,
// e-mail e demais dados em linhas compactas que quebram conforme o
// espaço. O clique continua abrindo o cadastro (como antes).
export const UserListSocioColumn = () => {
  return (
    <FunctionField
      label="Dados do Sócio"
      render={(record: any) => {
        const name = String(record.fullName || "").trim();
        return (
          <Box
            onClick={() => {
              window.location.href = `/#/users/crud/${record.id}/show`;
            }}
            sx={{
              display: "flex",
              gap: 1.25,
              alignItems: "flex-start",
              cursor: "pointer",
              minWidth: 0,
              "&:hover .socio-nome": { textDecoration: "underline" },
            }}
          >
            <Avatar
              src={socioPhotoUrl(record)}
              sx={{
                width: 32,
                height: 32,
                fontSize: 12,
                fontWeight: 700,
                backgroundColor: colors.bgContent,
                color: colors.primaryDark,
              }}
            >
              {name ? initials(name) : null}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              {/* nome inteiro em uma linha; se a célula cortar (…),
                  o hover mostra o nome completo no tooltip do tema */}
              <EllipsisTip text={name || record.email || "—"}>
                <Typography
                  className="socio-nome"
                  noWrap
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: colors.primaryDark,
                  }}
                >
                  {name || record.email || "—"}
                </Typography>
              </EllipsisTip>
              {record.email ? (
                <EllipsisTip text={record.email}>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: 12,
                      color: colors.textSecondary,
                      maxWidth: 240,
                    }}
                  >
                    {record.email}
                  </Typography>
                </EllipsisTip>
              ) : null}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  columnGap: 1,
                  rowGap: 0.25,
                  marginTop: 0.25,
                }}
              >
                {record.cpf ? (
                  <Typography
                    component="span"
                    sx={{ ...metaSx, fontFamily: fontFamilyMono }}
                  >
                    CPF {record.cpf}
                  </Typography>
                ) : null}
                {/* Coletivos migrados têm o documento no campo próprio —
                    sem isto, a linha ficava sem documento nenhum */}
                {record.cnpj ? (
                  <Typography
                    component="span"
                    sx={{ ...metaSx, fontFamily: fontFamilyMono }}
                  >
                    CNPJ {record.cnpj}
                  </Typography>
                ) : null}
                {record.CREA ? (
                  <Typography component="span" sx={metaSx}>
                    CREA {record.CREA}
                  </Typography>
                ) : null}
                {record.maritalStatus ? (
                  <Typography component="span" sx={metaSx}>
                    {maritalLabel(record.maritalStatus)}
                  </Typography>
                ) : null}
                {record.phoneNumber ? (
                  <Typography component="span" sx={metaSx}>
                    Cel. {formatPhone(record.phoneNumber)}
                  </Typography>
                ) : null}
                {record.professionalActivity ? (
                  <EllipsisTip text={record.professionalActivity}>
                    <Typography
                      component="span"
                      sx={{
                        ...metaSx,
                        maxWidth: 220,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {record.professionalActivity}
                    </Typography>
                  </EllipsisTip>
                ) : null}
                <Tooltip
                  title={
                    record.hasPhysicalCard
                      ? "Possui carteirinha física"
                      : "Sem carteirinha física"
                  }
                >
                  <Typography component="span" sx={metaSx}>
                    Carteirinha: {record.hasPhysicalCard ? "Sim" : "Não"}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        );
      }}
    />
  );
};
